/**
 * Special functions for the statistics layer.
 *
 * Accuracy in the *tails* is the requirement that drives the choices here.
 * A tolerance tool is routinely asked about 1 PPM defect rates, which live at
 * ~4.75 sigma; an approximation that is excellent near the mean and sloppy at
 * 5 sigma would corrupt exactly the numbers users care most about.
 */

/**
 * Standard normal CDF, via Hart's rational approximation.
 *
 * Accurate to ~1e-15 absolute, and — unlike the common Abramowitz–Stegun
 * 7.1.26 form — retains relative accuracy far into the tail.
 */
export function normalCdf(x: number): number {
  if (Number.isNaN(x)) return NaN;
  const z = Math.abs(x);
  let c: number;

  if (z > 37) {
    c = 0;
  } else {
    const e = Math.exp((-z * z) / 2);
    if (z < 7.07106781186547) {
      let b = 3.52624965998911e-2 * z + 0.700383064443688;
      b = b * z + 6.37396220353165;
      b = b * z + 33.912866078383;
      b = b * z + 112.079291497871;
      b = b * z + 221.213596169931;
      b = b * z + 220.206867912376;
      let d = 8.83883476483184e-2 * z + 1.75566716318264;
      d = d * z + 16.064177579207;
      d = d * z + 86.7807322029461;
      d = d * z + 296.564248779674;
      d = d * z + 637.333633378831;
      d = d * z + 793.826512519948;
      d = d * z + 440.413735824752;
      c = (e * b) / d;
    } else {
      // Continued-fraction tail expansion.
      let b = z + 0.65;
      b = z + 4 / b;
      b = z + 3 / b;
      b = z + 2 / b;
      b = z + 1 / b;
      c = e / (b * 2.506628274631);
    }
  }

  return x > 0 ? 1 - c : c;
}

// --- Inverse normal CDF: Wichura's AS241 (PPND16) ---------------------------
// Chosen over the shorter Beasley–Springer–Moro form because BSM degrades to
// ~1e-9 beyond 4 sigma, which is inside the range this tool reports on.

const A = [
  3.387132872796366608, 133.14166789178437745, 1971.5909503065514427,
  13731.693765509461125, 45921.953931549871457, 67265.770927008700853,
  33430.575583588128105, 2509.0809287301226727,
];
const B = [
  1, 42.313330701600911252, 687.1870074920579083, 5394.1960214247511077,
  21213.794301586595867, 39307.89580009271061, 28729.085735721942674,
  5226.495278852854561,
];
const C = [
  1.42343711074968357734, 4.6303378461565452959, 5.7694972214606914055,
  3.64784832476320460504, 1.27045825245236838258, 0.24178072517745061177,
  0.0227238449892691845833, 7.7454501427834140764e-4,
];
const D = [
  1, 2.05319162663775882187, 1.6763848301838038494, 0.68976733498510000455,
  0.14810397642748007459, 0.0151986665636164571966, 5.475938084995344946e-4,
  1.05075007164441684324e-9,
];
const E = [
  6.6579046435011037772, 5.4637849111641143699, 1.7848265399172913358,
  0.29656057182850489123, 0.026532189526576123093, 0.0012426609473880784386,
  2.71155556874348757815e-5, 2.01033439929228813265e-7,
];
const F = [
  1, 0.59983220655588793769, 0.13692988092273580531, 0.0148753612908506148525,
  7.868691311456132591e-4, 1.8463183175100546818e-5, 1.4215117583164458887e-7,
  2.04426310338993978564e-15,
];

/**
 * Horner evaluation, unrolled.
 *
 * `normalInv` is called once per normal or lognormal draw — tens of millions of
 * times in a large run — and a loop over a boxed `number[]` costs more than the
 * eight multiply-adds it performs. The arrays above remain the readable
 * statement of the coefficients; this is how they are actually evaluated.
 */
function poly(c: number[], r: number): number {
  return (
    c[0] +
    r *
      (c[1] +
        r * (c[2] + r * (c[3] + r * (c[4] + r * (c[5] + r * (c[6] + r * c[7]))))))
  );
}

/**
 * Inverse standard normal CDF (probit). Accurate to ~1e-16 over (0,1).
 *
 * This is the workhorse of the sampling layer: every distribution is sampled
 * by inverse transform, which keeps one uniform draw mapped to one variate and
 * therefore keeps the counter-based PRNG's position addressing intact
 * (see prng.ts). Rejection methods like Ziggurat are faster per draw but
 * consume a variable number of uniforms, which would destroy that property.
 */
export function normalInv(p: number): number {
  if (Number.isNaN(p) || p < 0 || p > 1) return NaN;
  if (p === 0) return -Infinity;
  if (p === 1) return Infinity;

  const q = p - 0.5;

  if (Math.abs(q) <= 0.425) {
    const r = 0.180625 - q * q;
    return (q * poly(A, r)) / poly(B, r);
  }

  let r = q < 0 ? p : 1 - p;
  r = Math.sqrt(-Math.log(r));

  let value: number;
  if (r <= 5) {
    r -= 1.6;
    value = poly(C, r) / poly(D, r);
  } else {
    r -= 5;
    value = poly(E, r) / poly(F, r);
  }

  return q < 0 ? -value : value;
}

/** Standard normal PDF. */
export function normalPdf(x: number): number {
  return Math.exp((-x * x) / 2) / 2.5066282746310002;
}

/** Error function, expressed through the normal CDF to avoid a second approximation. */
export function erf(x: number): number {
  return 2 * normalCdf(x * Math.SQRT2) - 1;
}
